import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { IdGovLoginButton } from "@/components/auth/IdGovLoginButton";
import { Spinner } from "@/components/ui/spinner";
import { LogoIcon } from "@/components/ui/logo";

function FuturisticBackground() {
  return (
    <div className="fixed inset-0 -z-10 overflow-hidden bg-gradient-to-br from-amber-100/80 via-yellow-50 to-orange-100/60">
      <div 
        className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full blur-[120px]"
        style={{ 
          background: "linear-gradient(135deg, rgba(251,191,36,0.5) 0%, rgba(245,158,11,0.3) 100%)",
          animation: "morph1 15s ease-in-out infinite"
        }} 
      />
      
      <div 
        className="absolute -bottom-40 -right-40 w-[700px] h-[700px] rounded-full blur-[140px]"
        style={{ 
          background: "linear-gradient(225deg, rgba(217,119,6,0.4) 0%, rgba(251,191,36,0.2) 100%)",
          animation: "morph2 18s ease-in-out infinite"
        }} 
      />
      
      <div 
        className="absolute top-1/3 right-1/4 w-[400px] h-[400px] rounded-full blur-[100px]"
        style={{ 
          background: "radial-gradient(circle, rgba(253,224,71,0.4) 0%, transparent 70%)",
          animation: "morph3 12s ease-in-out infinite"
        }} 
      />

      <div 
        className="absolute bottom-1/4 left-1/4 w-[300px] h-[300px] rounded-full blur-[80px]"
        style={{ 
          background: "radial-gradient(circle, rgba(251,191,36,0.35) 0%, transparent 70%)",
          animation: "morph4 20s ease-in-out infinite"
        }} 
      />

      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full blur-[180px] opacity-40"
        style={{ 
          background: "conic-gradient(from 0deg, rgba(251,191,36,0.3), rgba(245,158,11,0.2), rgba(217,119,6,0.3), rgba(251,191,36,0.3))",
          animation: "rotate 30s linear infinite"
        }} 
      />

      <div className="absolute inset-0 backdrop-blur-[1px]" />

      <style>{`
        @keyframes morph1 {
          0%, 100% { transform: translate(0, 0) scale(1) rotate(0deg); }
          33% { transform: translate(50px, 30px) scale(1.1) rotate(5deg); }
          66% { transform: translate(-30px, 50px) scale(0.95) rotate(-5deg); }
        }
        @keyframes morph2 {
          0%, 100% { transform: translate(0, 0) scale(1) rotate(0deg); }
          33% { transform: translate(-60px, -40px) scale(1.15) rotate(-3deg); }
          66% { transform: translate(40px, -60px) scale(0.9) rotate(3deg); }
        }
        @keyframes morph3 {
          0%, 100% { transform: scale(1); opacity: 0.4; }
          50% { transform: scale(1.3); opacity: 0.6; }
        }
        @keyframes morph4 {
          0%, 100% { transform: scale(1) translateY(0); }
          50% { transform: scale(1.2) translateY(-30px); }
        }
        @keyframes rotate {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export function Home() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-100 via-yellow-50 to-orange-100 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    const from = (location.state as { from?: string })?.from || "/dashboard";
    return <Navigate to={from} replace />;
  }

  return (
    <div className="min-h-screen flex flex-col relative">
      <FuturisticBackground />

      <main className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-8 shadow-xl shadow-amber-500/10 border border-white/50">
            <div className="text-center mb-8">
              <LogoIcon size="lg" className="mx-auto mb-5" />
              <h1 className="text-2xl font-bold text-gray-900">Світлодім</h1>
              <p className="text-gray-600 text-sm mt-1">
                Кабінет програми енергоефективності
              </p>
            </div>

            <div className="space-y-4">
              <IdGovLoginButton redirectPath="/dashboard" />
              <p className="text-xs text-center text-gray-500 pt-1">
                Авторизація через захищену систему ID.GOV.UA
              </p>
            </div>
          </div>
        </div>
      </main>

      <footer className="py-6 text-center relative z-10">
        <p className="text-xs text-gray-600">
          © 2026, Власність Міністерства розвитку громад та територій України
        </p>
      </footer>
    </div>
  );
}

export default Home;
